/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import Lenis from 'lenis';
import { ReactLenis } from 'lenis/react';
import React, { useEffect, useRef } from 'react';

import gsap from 'gsap';
import { useGlobalState } from '../../state/globalStateContext';
import { BackgroundColorTheme } from '../../templates/Page';
import {
  useActivateGoogleOptimize,
  useStoreReferrerOnLoad,
  useStoreUTMParamsOnLoad,
} from '../../utils/hooks';
import { clsx } from '../../utils/utils';
import Background, { useThemeFromScroll } from './Background';
import CookiesBanner from './CookiesBanner';
import Footer, { FooterProps } from './Footer';
import Header, { HeaderProps } from './Header';
import * as styles from './Layout.module.scss';

type LenisRef = {
  wrapper?: HTMLElement;
  content?: HTMLElement;
  lenis?: Lenis;
};

interface LayoutProps {
  pageTheme?: BackgroundColorTheme;
  children: React.ReactNode;
  headerConfig: HeaderProps;
  footerConfig: FooterProps;
  cookiesBannerConfig?: {
    textContent?: React.ReactNode;
  };
  frontCloudPosition?: 'fullScreen' | 'top' | 'bottom';
  hideCookieBar?: boolean;
}

const Layout = ({
  pageTheme,
  children,
  headerConfig,
  footerConfig,
  cookiesBannerConfig,
  frontCloudPosition,
  hideCookieBar,
}: LayoutProps): React.ReactElement => {
  const isHidden = useActivateGoogleOptimize();
  const { setIsEmbedded } = useGlobalState();

  useStoreUTMParamsOnLoad();
  useStoreReferrerOnLoad();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const sanityPreviewParam = urlSearchParams.get('_sanityPreview');

    setIsEmbedded(window.parent !== window && !sanityPreviewParam);
  }, []);

  const lenisRef = useRef<LenisRef | null>(null);

  useEffect(() => {
    function update(time) {
      lenisRef.current?.lenis?.raf(time * 1000);
    }
    gsap.ticker.add(update);
    return () => {
      gsap.ticker.remove(update);
    };
  });

  const themeFromScroll = useThemeFromScroll();

  return (
    <ReactLenis
      ref={lenisRef}
      root
      autoRaf={false}
      options={{
        easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      }}
    >
      <div className={styles.container} style={isHidden ? { opacity: 0 } : undefined}>
        <Background
          withTopClouds={frontCloudPosition === 'fullScreen' || frontCloudPosition === 'top'}
          withBottomClouds={frontCloudPosition === 'fullScreen' || frontCloudPosition === 'bottom'}
        />
        <Header {...headerConfig} theme={themeFromScroll || pageTheme}></Header>
        <div
          role="main"
          className={clsx(
            styles.main,
            (frontCloudPosition === 'bottom' || frontCloudPosition === 'fullScreen') &&
              styles.fullHeight,
          )}
          data-theme={pageTheme}
        >
          <div className={styles.content}>{children}</div>
        </div>
        <Footer {...footerConfig}></Footer>
        {cookiesBannerConfig?.textContent && (
          <CookiesBanner
            className={clsx(styles.cookiesBanner, hideCookieBar && styles.cookiesBannerHidden)}
            textContent={cookiesBannerConfig.textContent}
            theme={themeFromScroll || pageTheme}
          />
        )}
      </div>
    </ReactLenis>
  );
};

export default Layout;
